// Capnolog brand identity — THE one file a solo founder hand-edits.
//
// Everything brand-specific (name, domain, emails, the App Store link, launch
// state) lives here. App-derived content (palette, ACGME data, changelog,
// privacy copy) is generated/imported from the app's source of truth — never
// hand-edited (see scripts/sync-landing.mjs + the @caselogtracker/core imports).
//
// EXTENSION STORE LINKS ARE NOT HERE. They used to be — a hand-typed Chrome Web
// Store URL with the listing slug baked in, which is exactly the kind of string
// that silently rots (the one that lived here had an un-encoded em-dash in the
// slug). They now come from generated/extension.ts, which sync-landing.mjs
// builds from apps/extension/store.json — the one machine-readable record of the
// extension's ids and per-browser listing state, guarded by lint:extension-ids.
// To publish a browser: set its id in store.json, not here.

export interface BrandConfig {
  /** Display name used everywhere in copy and the wordmark. */
  readonly name: string;
  /** Bare domain (no scheme), for display + email construction. */
  readonly domain: string;
  /** Canonical origin used for SEO/OG/canonical/sitemap. No trailing slash. */
  readonly siteUrl: string;
  /** Support inbox; rendered as a mailto: on /support + footer. */
  readonly supportEmail: string;
  /** One-line product promise; reused in hero + meta description fallbacks. */
  readonly tagline: string;
  /** Real apps.apple.com URL once the app is public; null → beta CTA stands in. */
  readonly appStoreUrl: string | null;
  /** Real play.google.com URL once the Android app is public; null → nothing renders. */
  readonly playStoreUrl: string | null;
  /**
   * Apple's provider token — the `pt=` in every campaign link App Store
   * Connect generates (App Analytics → Acquisition → Campaigns → Generate
   * Campaign Link). All digits, and public: it is printed in every campaign URL.
   * With it set, a press on any link to `appStoreUrl` is tagged
   * `ct=web-<source>` so the Campaigns report splits the site's downloads by
   * channel (src/lib/store-campaign.ts, docs/ops/campaign-links.md). Null →
   * store links go out untagged, exactly as before.
   */
  readonly appStoreProviderToken: string | null;
  /**
   * App Store custom product pages (D-319), keyed by the site path whose
   * badges open each one: an audience or specialty slug. scripts/asc/cpp.py
   * creates a page and prints its id; withProductPage (src/lib/store-campaign.ts)
   * adds the id to that path's App Store badge as `?ppid=`. Apple shows the
   * default listing until the page is approved and visible, so an id can go in
   * as soon as the page exists. The desktop QR code stays on the default page.
   */
  readonly appStoreProductPages: Readonly<Record<string, string>>;
  /**
   * THE GA SWITCH (2026-09-02). Specialty ids (src/data/waitlist-options.ts)
   * whose RESIDENTS are sent to the stores instead of the beta list. Empty
   * while everything is in beta; `["anesthesia"]` on the day anesthesia goes
   * GA — the beta form keeps taking every other specialty, and fellows and
   * students in a GA specialty too (they are not who GA is for yet). Has no
   * effect until `appStoreUrl` is set as well: a store panel with no link
   * would strand the reader.
   */
  readonly generallyAvailableSpecialtyIds: readonly string[];
  /** Optional: repo / instructions for the dev "load unpacked" extension build. */
  readonly extensionRepoUrl: string | null;
  /**
   * Capnolog's own public profiles, in the order /about lists them. Each `url`
   * is the Organization's schema.org `sameAs` (prerender.mjs writes them into
   * index.html's site-wide graph), and /about prints every one, because markup
   * on this site only states what a reader can see. Add a profile only once
   * the account is real and public under the Capnolog name.
   */
  readonly sameAs: readonly { readonly label: string; readonly url: string }[];
}

export const BRAND: BrandConfig = {
  name: "Capnolog",
  domain: "capnolog.com",
  siteUrl: "https://capnolog.com",
  supportEmail: "support@capnolog.com",
  tagline: "Log any ACGME case in seconds.",
  // GA (2026-09-22): the App Store listing is approved. The id form of the
  // listing URL resolves without the marketing slug, which Apple is free to
  // change; APP_STORE_APP_ID in lib/pricing is the same id.
  appStoreUrl: "https://apps.apple.com/app/id6781694116",
  playStoreUrl: null,
  // Owner-supplied 2026-09-29, from the campaign link App Store Connect
  // generated (docs/ops/campaign-links.md). Apple's team number, not a secret.
  appStoreProviderToken: "126164298",
  // From `scripts/asc/cpp.py status` (chain 1x, created 2026-10-16; the
  // anesthesiology page 2026-10-17). Until a page is approved its ppid opens
  // the default page, so a badge may carry the id before review.
  appStoreProductPages: {
    "medical-students": "3545f2b1-2d46-479e-8e9a-69a05137ad09",
    neurosurgery: "bfd34cef-81d0-4c0c-99bf-2f68b8ff6e39",
    anesthesia: "c417e561-eb21-41c9-bc1f-243527ec4372",
  },
  // A waitlist-option id (src/data/waitlist-options.ts), not a registry row:
  // this file is the import root and cannot read the registry without a cycle.
  generallyAvailableSpecialtyIds: ["anesthesia", "neurosurgery"], // specialty-literal-allow — see line above
  extensionRepoUrl: null,
  sameAs: [
    { label: "LinkedIn", url: "https://www.linkedin.com/company/capnolog/" },
    { label: "Instagram", url: "https://www.instagram.com/capnolog/" },
    { label: "X", url: "https://x.com/Capnolog" },
  ],
};

// ── FOUNDER ───────────────────────────────────────────────────────────────────
//
// THE ONE PLACE THE SITE NAMES WHO BUILDS CAPNOLOG (D-99, ruled 2026-09-13).
// Name and degree are published; the residency program is not, because a
// resident writing publicly about their own program's logging is a different
// disclosure than this one. The /about signature, the guide byline, the press
// kit line and the schema.org Person node (`#founder`, written into index.html
// by prerender.mjs) all read these fields. `trainingYear` changes every July.

export const FOUNDER = {
  name: "Christopher Cutler",
  honorificSuffix: "MD",
  trainingYear: "CA-1",
  field: "anesthesiology",
  jobTitle: "Anesthesiology resident",
} as const;

/** "Christopher Cutler, MD, CA-1, anesthesiology" — the site's one signature. */
export const FOUNDER_LINE = `${FOUNDER.name}, ${FOUNDER.honorificSuffix}, ${FOUNDER.trainingYear}, ${FOUNDER.field}`;

/** Pre-built mailto: with a subject line, for the support link. */
export const SUPPORT_MAILTO = `mailto:${BRAND.supportEmail}?subject=${encodeURIComponent(
  `${BRAND.name} — support`,
)}`;

// ── PLATFORMS ───────────────────────────────────────────────────────────────
//
// THE ONE PLACE THE SITE LEARNS WHICH DEVICES CAPNOLOG RUNS ON (2026-09-02).
//
// Before this existed the answer was spelled out by hand in about thirty
// sentences — "iPhone only", "there is no Android app", "iOS 17 or later" — and
// the day the Android port landed on `main` every one of them became a claim the
// product disproves. A page now asks this table instead of asserting a platform,
// so the next state change (a Play listing, the Mac companion shipping) is one
// edit here.
//
// WHAT IS TRUE TODAY, and where each fact was verified:
//   • iPhone — shipping, iOS 17 or later (apps/mobile/app.config.ts
//     `iosDeploymentTarget: "17.0"`). On the App Store since GA (2026-09-22):
//     `appStoreUrl` is set, so the badge and the "download" verb render.
//   • Android — shipping, Android 7.0 or later (minSdk 24, recorded in
//     plans/archive/072-android-port.md §"APK"). Distributed as a SIGNED APK we send
//     to the resident. `playStoreUrl` is null because there is no Play listing,
//     and NOTHING on the site may say "on Google Play" until there is one.
//   • Mac — a desk companion, in development. Not installable by anyone.
//
// THE ANDROID CTA. While `playStoreUrl` is null the only honest route is the
// beta form, which is where the invite is arranged. `platformBetaHref` builds
// that link and tags it with the platform so the form can say back which build
// was asked for. THE TAG IS DISPLAY ONLY — it is never submitted: the API's
// beta-waitlist body is a `z.strictObject`, so an unknown `platform` field
// would be a 400 on every Android signup.

/** The devices the site knows about. */
export type PlatformId = "ios" | "android" | "mac";

export type PlatformStatus =
  /** Installable today by someone we invite. */
  | "available"
  /** Being built. Every sentence about it must say so. */
  | "coming_soon";

/** How a build reaches a resident. Only meaningful for an available platform. */
export type PlatformDistribution =
  /** A store listing anyone can open. */
  | "store"
  /** An invited build: TestFlight on iOS, a signed APK we send on Android. */
  | "direct";

export interface Platform {
  readonly id: PlatformId;
  /** What a reader calls the device. "iPhone", not "iOS". */
  readonly label: string;
  readonly status: PlatformStatus;
  readonly distribution: PlatformDistribution;
  /** The OS floor, phrased for a reader. Null where there is nothing to install. */
  readonly minOs: string | null;
  /**
   * The public listing, once one exists. `null` means there is no page to send
   * anyone to, and the CTA falls back to the beta form.
   */
  readonly storeUrl: string | null;
  /** The CTA label. Never promises a store the platform does not have. */
  readonly ctaLabel: string;
  /** One sentence a page can print under the CTA, true as written today. */
  readonly note: string;
}

export interface Platforms {
  readonly ios: Platform;
  readonly android: Platform;
  readonly mac: Platform;
}

export const PLATFORMS: Platforms = {
  ios: {
    id: "ios",
    label: "iPhone",
    status: "available",
    distribution: BRAND.appStoreUrl ? "store" : "direct",
    minOs: "iOS 17 or later",
    storeUrl: BRAND.appStoreUrl,
    ctaLabel: BRAND.appStoreUrl
      ? "Get the iPhone app"
      : "Join the private beta",
    note: BRAND.appStoreUrl
      ? "iOS 17 or later. On the App Store."
      : "iOS 17 or later. Invites go out through TestFlight.",
  },
  android: {
    id: "android",
    label: "Android",
    status: "available",
    distribution: BRAND.playStoreUrl ? "store" : "direct",
    minOs: "Android 7.0 or later",
    storeUrl: BRAND.playStoreUrl,
    // The CTA names what the click PRODUCES. With no Play listing it scrolls to
    // the beta form, which files a request and sends an invite later — so "Get"
    // would promise a download the page cannot hand over.
    ctaLabel: BRAND.playStoreUrl
      ? "Get the Android app"
      : "Ask for the Android build",
    note: "Android 7.0 or later. Capnolog sends you a signed build to install.",
  },
  mac: {
    id: "mac",
    label: "Mac",
    status: "coming_soon",
    distribution: "direct",
    minOs: null,
    storeUrl: null,
    ctaLabel: "Coming soon",
    note: "A Mac companion for the desk is in progress and not yet available.",
  },
};

/** The two platforms a resident can install today, in the order pages show them. */
export const AVAILABLE_PLATFORMS: readonly Platform[] = [
  PLATFORMS.ios,
  PLATFORMS.android,
];

/** Both phones ship the same build from one codebase. Used in copy, not logic. */
export const PHONE_PLATFORMS_PHRASE = "iPhone and Android";

/**
 * The query key the beta form reads to say which build a reader asked for.
 * DISPLAY ONLY — see the section header. Not a wire field.
 */
export const PLATFORM_QUERY_KEY = "platform";

/** True for a value that names a platform. */
export function isPlatformId(
  value: string | null | undefined,
): value is PlatformId {
  return value === "ios" || value === "android" || value === "mac";
}

/**
 * Where a platform's CTA goes.
 *
 * A listing when there is one; otherwise the page's own beta anchor, tagged
 * with the platform. `betaHref` is the anchor the calling page uses for its
 * form ("#beta" on a page that carries one, "/download#beta" from a page that
 * does not), so the link never leaves the reader on a page with no form.
 */
export function platformBetaHref(platform: Platform, betaHref: string): string {
  if (platform.storeUrl) return platform.storeUrl;
  const hashAt = betaHref.indexOf("#");
  const path = hashAt === -1 ? betaHref : betaHref.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : betaHref.slice(hashAt);
  return `${path}?${PLATFORM_QUERY_KEY}=${platform.id}${hash}`;
}
